import React, { useEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Card, Input, Button, Space, Typography, Spin } from 'antd';
import { RobotOutlined, SendOutlined, AudioOutlined, AudioMutedOutlined } from '@ant-design/icons';
import axios from 'axios';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { getHeaders } from '../../utility/getHeader';

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

const SUGGESTED_QUESTIONS = [
  'How did sales perform last week?',
  'Which items are selling best this month?',
  'Are any assets under maintenance right now?',
];

const SpeechRecognitionApi = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function AiReports() {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!SpeechRecognitionApi) return;

    const recognition = new SpeechRecognitionApi();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join(' ');
      setQuestion(transcript);

      if (event.results[event.results.length - 1].isFinal) {
        handleAsk(transcript);
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error('Voice input failed. Please try again or type your question.');
      }
    };

    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleListening = () => {
    if (!SpeechRecognitionApi) {
      toast.error('Voice input is not supported in this browser. Please type your question instead.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }
    setQuestion('');
    setAnswer(null);
    setIsListening(true);
    recognitionRef.current.start();
  };

  const handleAsk = async (spokenQuestion) => {
    const finalQuestion = (spokenQuestion ?? question).trim();
    if (!finalQuestion) {
      toast.error('Please ask a question, by voice or by typing');
      return;
    }

    setLoading(true);
    setAnswer(null);
    try {
      const res = await axios.post('/ai-reports/query', { question: finalQuestion }, getHeaders());
      setAnswer(res.data?.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('AI Reports is available to admins only.');
      } else if (err.response?.status === 503) {
        toast.error(err.response.data?.msg || 'AI Reports is not available right now.');
      } else {
        toast.error('Could not generate the report. Please try again.');
      }
      console.error('AI Reports error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Header title="AI Reports" />
      <ToastContainer />

      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>Ask about your business</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space align="start" style={{ width: '100%' }}>
            <Button
              shape="circle"
              size="large"
              danger={isListening}
              type={isListening ? 'primary' : 'default'}
              icon={isListening ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={toggleListening}
              title={isListening ? 'Stop listening' : 'Ask by voice'}
            />
            <TextArea
              rows={3}
              style={{ flex: 1 }}
              placeholder={'Tap the mic and ask - e.g. "How did sales do last week?" - or type here.'}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleAsk();
                }
              }}
            />
          </Space>

          {isListening && (
            <Text type="danger">
              <Spin size="small" style={{ marginRight: 8 }} />
              Listening... speak your question, including the time period (e.g. "this month").
            </Text>
          )}

          <Space wrap>
            {SUGGESTED_QUESTIONS.map((q) => (
              <Button key={q} size="small" onClick={() => setQuestion(q)}>
                {q}
              </Button>
            ))}
          </Space>

          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => handleAsk()}>
            Ask
          </Button>
        </Space>
      </Card>

      {loading && (
        <Card>
          <Spin /> <Text type="secondary" style={{ marginLeft: 8 }}>Analyzing your business data...</Text>
        </Card>
      )}

      {!loading && answer && (
        <Card title="Answer">
          <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 15 }}>{answer.answer}</Paragraph>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Based on data from {answer.range?.from} to {answer.range?.to}
          </Text>
        </Card>
      )}
    </Layout>
  );
}
